/**
 * «Цели сезона» — декомпозиция целей бюджета (ADR-069, экран v2-06).
 *
 * УК спускает не квоты по культурам, а целевые числа: выручка и прибыль на
 * трёх уровнях — холдинг / кластеры / хозяйства («каждый получает свой план»,
 * founder 19.08). Уровни независимы: холдинговое число НЕ раскладывается вниз
 * автоматически — раскладка целей это управленческое решение УК, система лишь
 * фиксирует её и подсвечивает расхождение «сумма по хозяйствам ≠ цель
 * кластера» (амбер-подсказка, не блокер: УК вправе держать резерв).
 *
 * Права: форма пишет только с `budget_targets:U` (УК и админ компании);
 * остальные видят read-only. Цифры в ₽ — под `budget_catalog:R`: роль без
 * доступа к деньгам видит только процент прибыли.
 */
import { useState } from 'react';
import { useParams } from '@tanstack/react-router';
import { AppShell } from '@/components/layout/AppShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Stack } from '@/components/ui/stack';
import { useCan } from '@/lib/auth/usePermissions';
import {
  consistencyHint,
  formatTargetMln,
  parseMoneyDraft,
  parsePctDraft,
  useSaveTarget,
  useTargetsTree,
  type BudgetTarget,
  type TargetConsistency,
  type TargetLevel,
} from '@/lib/planning/budgetTargets';
import { cn } from '@/lib/utils';
import { useSeasonByYear } from '@/lib/planning/useSeasonByYear';
import { SeasonNotStartedCard, SeasonYearOutOfWindowCard } from './SeasonBudgetStates';

export function BudgetTargetsPage() {
  const params = useParams({ strict: false }) as { year?: string };
  const year = Number(params.year);
  const season = useSeasonByYear(year);
  const canRead = useCan('budget_targets', 'r');
  const tree = useTargetsTree(canRead ? season.seasonId : null);
  const save = useSaveTarget(season.seasonId);

  if (!season.validYear || season.missing) {
    return (
      <Shell year={params.year}>
        <SeasonYearOutOfWindowCard year={year} />
      </Shell>
    );
  }
  if (season.isLoading || (canRead && season.seasonId && tree.isLoading)) {
    return (
      <Shell year={params.year}>
        <Skeleton className="h-64 w-full" />
      </Shell>
    );
  }
  if (season.notOpened) {
    return (
      <Shell year={params.year}>
        <SeasonNotStartedCard year={year} onOpened={() => undefined} />
      </Shell>
    );
  }
  if (!canRead) {
    return (
      <Shell year={params.year}>
        <Alert>
          <AlertDescription>У вас нет доступа к целям бюджета сезона.</AlertDescription>
        </Alert>
      </Shell>
    );
  }
  if (tree.isError) {
    return (
      <Shell year={params.year}>
        <Alert variant="destructive">
          <AlertDescription>Не удалось загрузить цели сезона. Обновите страницу.</AlertDescription>
        </Alert>
      </Shell>
    );
  }
  const data = tree.data;
  if (!data) return <Shell year={params.year}>{null}</Shell>;

  const onSave = (level: TargetLevel, objectId: string | null, values: TargetValues) => {
    save.mutate({
      level,
      object_id: objectId,
      target_revenue: values.revenue,
      target_margin: values.margin,
      target_margin_pct: values.marginPct,
      source_note: values.sourceNote,
    });
  };

  return (
    <Shell year={params.year}>
      {!data.money_visible && (
        <Alert>
          <AlertDescription>
            Цели в рублях не показаны: они считаются деньгами бюджета, а доступа к ним у вас нет.
            Процент прибыли виден полностью.
          </AlertDescription>
        </Alert>
      )}
      {!data.can_edit && (
        <p className="text-sm text-muted-foreground">
          Цели ставит управляющая компания — здесь они только читаются.
        </p>
      )}

      <Card>
        <CardContent className="p-4">
          <SectionTitle title={`Холдинг · ${data.company_name}`} note="цель всей компании на сезон" />
          <TargetRow
            rowKey={`company-${data.company_id}`}
            name={data.company_name}
            target={data.company_target}
            canEdit={data.can_edit}
            moneyVisible={data.money_visible}
            busy={save.isPending}
            onSave={(v) => onSave('company', null, v)}
          />
          <ConsistencyNote consistency={data.consistency} childrenWord="кластерам и хозяйствам" />
        </CardContent>
      </Card>

      {data.clusters.map((cluster) => (
        <Card key={cluster.id}>
          <CardContent className="p-4">
            <SectionTitle title={`Кластер · ${cluster.name}`} note="своя цель + цели его хозяйств" />
            <TargetRow
              rowKey={`cluster-${cluster.id}`}
              name={cluster.name}
              target={cluster.target}
              canEdit={data.can_edit}
              moneyVisible={data.money_visible}
              busy={save.isPending}
              onSave={(v) => onSave('cluster', cluster.id, v)}
            />
            <ConsistencyNote consistency={cluster.consistency} childrenWord="хозяйствам" />
            <div className="mt-2 border-l-2 border-border pl-4">
              {cluster.farms.map((farm) => (
                <TargetRow
                  key={farm.id}
                  rowKey={`farm-${farm.id}`}
                  name={farm.name}
                  target={farm.target}
                  canEdit={data.can_edit}
                  moneyVisible={data.money_visible}
                  busy={save.isPending}
                  onSave={(v) => onSave('farm', farm.id, v)}
                />
              ))}
              {cluster.farms.length === 0 && (
                <p className="py-2 text-sm text-muted-foreground">В кластере пока нет хозяйств.</p>
              )}
            </div>
          </CardContent>
        </Card>
      ))}

      {data.farms.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <SectionTitle title="Хозяйства без кластера" note="прямые цели от холдинга" />
            {data.farms.map((farm) => (
              <TargetRow
                key={farm.id}
                rowKey={`farm-${farm.id}`}
                name={farm.name}
                target={farm.target}
                canEdit={data.can_edit}
                moneyVisible={data.money_visible}
                busy={save.isPending}
                onSave={(v) => onSave('farm', farm.id, v)}
              />
            ))}
          </CardContent>
        </Card>
      )}

      <p className="text-xs leading-5 text-muted-foreground">
        Уровни независимы: цель хозяйства — не «кусок» холдинговой, а собственный план. Расхождение
        суммы с уровнем выше — подсказка для управляющей компании, сохранить её не мешает. Недостижение
        цели вариантом плана — подсветка в сводке, утверждать вариант оно не запрещает.
      </p>
    </Shell>
  );
}

function SectionTitle({ title, note }: { title: string; note: string }) {
  return (
    <div className="mb-2 flex flex-wrap items-baseline gap-2">
      <h2 className="text-sm font-semibold text-foreground">{title}</h2>
      <span className="ml-auto text-xs text-muted-foreground">{note}</span>
    </div>
  );
}

function ConsistencyNote({
  consistency,
  childrenWord,
}: {
  consistency: TargetConsistency;
  childrenWord: string;
}) {
  const hint = consistencyHint(consistency, childrenWord);
  if (!hint) return null;
  return <p className="mt-1 text-xs leading-5 text-accent-brand">{hint}</p>;
}

interface TargetValues {
  revenue: number | null;
  margin: number | null;
  marginPct: number | null;
  sourceNote: string | null;
}

/**
 * Строка объекта: имя + инлайн-инпуты «выручка ₽ / прибыль ₽ / прибыль %» +
 * источник числа. Сохранение — явной кнопкой, а не на blur: цель — редкое и
 * ответственное число, случайный blur не должен его переписывать.
 *
 * Невалидный ввод НЕ сохраняется и НЕ затирается (приём YieldCell, #817):
 * «abc» остаётся в поле с ошибкой словами — молчаливое превращение мусора в
 * «цель не задана» с зелёным тостом выглядело бы как «система съела ввод»
 * (блокер head-web на #828). Пустое поле — осознанная очистка.
 */
export function TargetRow({
  rowKey,
  name,
  target,
  canEdit,
  moneyVisible,
  busy,
  onSave,
}: {
  rowKey: string;
  name: string;
  target: BudgetTarget | null;
  canEdit: boolean;
  moneyVisible: boolean;
  busy: boolean;
  onSave: (values: TargetValues) => void;
}) {
  const [draft, setDraft] = useState<Record<string, string> | null>(null);
  const [errors, setErrors] = useState<Partial<Record<'revenue' | 'margin' | 'marginPct', string>>>({});
  const initial = {
    revenue: target?.target_revenue?.toString() ?? '',
    margin: target?.target_margin?.toString() ?? '',
    marginPct: target?.target_margin_pct?.toString() ?? '',
    sourceNote: target?.source_note ?? '',
  };
  const values = draft ?? initial;
  const dirty = draft !== null && JSON.stringify(draft) !== JSON.stringify(initial);

  if (!canEdit || !moneyVisible) {
    // Read-only строка: без права на запись (или без денег) форма не рисуется —
    // «кнопки, которые дадут 403» это UX-ловушка.
    return (
      <div className="flex min-h-10 flex-wrap items-center gap-x-4 gap-y-1 border-b border-border py-2 text-sm leading-6">
        <span className="text-foreground">{name}</span>
        <span className="ml-auto tabular-nums text-muted-foreground">
          выручка: {moneyVisible ? formatTargetMln(target?.target_revenue) : '—'}
        </span>
        <span className="tabular-nums text-muted-foreground">
          прибыль: {moneyVisible ? formatTargetMln(target?.target_margin) : '—'}
        </span>
        <span className="tabular-nums text-muted-foreground">
          {target?.target_margin_pct != null ? `${target.target_margin_pct} %` : '— %'}
        </span>
      </div>
    );
  }

  const set = (field: 'revenue' | 'margin' | 'marginPct' | 'sourceNote', value: string) => {
    setDraft({ ...values, [field]: value });
    // Правка поля снимает его ошибку — человек уже реагирует на подсказку.
    if (field !== 'sourceNote' && errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const commit = () => {
    // Валидируем ВСЕ поля разом: одна попытка сохранения — все подсказки сразу,
    // а не по одной за клик. Невалидный черновик остаётся в поле (см. докстринг).
    const revenue = parseMoneyDraft(values.revenue);
    const margin = parseMoneyDraft(values.margin, { allowNegative: true });
    const marginPct = parsePctDraft(values.marginPct);
    const nextErrors = {
      revenue: revenue.ok ? undefined : revenue.error,
      margin: margin.ok ? undefined : margin.error,
      marginPct: marginPct.ok ? undefined : marginPct.error,
    };
    setErrors(nextErrors);
    if (!revenue.ok || !margin.ok || !marginPct.ok) return;
    onSave({
      revenue: revenue.value,
      margin: margin.value,
      marginPct: marginPct.value,
      sourceNote: values.sourceNote.trim() || null,
    });
    setDraft(null);
  };

  const moneyField = (
    field: 'revenue' | 'margin',
    label: string,
    ariaLabel: string,
    width: string,
  ) => (
    <label className="flex items-center gap-1 text-xs text-muted-foreground">
      {label}
      <Input
        className={cn(
          `h-8 ${width} text-right tabular-nums`,
          errors[field] && 'border-destructive focus-visible:ring-destructive',
        )}
        inputMode="decimal"
        aria-label={ariaLabel}
        aria-invalid={errors[field] ? true : undefined}
        value={values[field]}
        onChange={(e) => set(field, e.target.value)}
      />
    </label>
  );

  const rowErrors = [errors.revenue, errors.margin, errors.marginPct].filter(Boolean);

  return (
    <div className="border-b border-border py-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="min-w-40 text-sm text-foreground">{name}</span>
        {moneyField('revenue', 'выручка, ₽', `Цель по выручке — ${name}`, 'w-36')}
        {moneyField('margin', 'прибыль, ₽', `Цель по прибыли — ${name}`, 'w-36')}
        <label className="flex items-center gap-1 text-xs text-muted-foreground">
          прибыль, %
          <Input
            className={cn(
              'h-8 w-20 text-right tabular-nums',
              errors.marginPct && 'border-destructive focus-visible:ring-destructive',
            )}
            inputMode="decimal"
            aria-label={`Цель по проценту прибыли — ${name}`}
            aria-invalid={errors.marginPct ? true : undefined}
            value={values.marginPct}
            onChange={(e) => set('marginPct', e.target.value)}
          />
        </label>
        <label className="flex flex-1 items-center gap-1 text-xs text-muted-foreground">
          источник
          <Input
            className="h-8 min-w-32 flex-1"
            placeholder="письмо дирекции, дата"
            aria-label={`Источник цели — ${name}`}
            value={values.sourceNote}
            onChange={(e) => set('sourceNote', e.target.value)}
          />
        </label>
        <Button key={rowKey} size="sm" variant="outline" disabled={!dirty || busy} onClick={commit}>
          Сохранить
        </Button>
      </div>
      {rowErrors.length > 0 && (
        <p className="mt-1 text-xs text-destructive" role="alert">
          {rowErrors.join('. ')}. Введённое не сохранено и осталось в поле — поправьте или очистите
          (пустое поле снимает цель).
        </p>
      )}
    </div>
  );
}

function Shell({ year, children }: { year?: string; children: React.ReactNode }) {
  return (
    <AppShell>
      <PageHeader
        title={`Цели сезона ${year ?? ''}`.trim()}
        description="Целевые выручка и прибыль от управляющей компании — на холдинг, кластеры и хозяйства. Каждый уровень получает свой план; сводка варианта сверяет факт с целью светофором."
        breadcrumbs={[
          { label: 'Планирование сезона', to: '/planning' },
          { label: `Бюджет сезона ${year ?? ''}`.trim() },
        ]}
      />
      <div className="mx-auto mt-4 flex max-w-5xl flex-col gap-4">
        <Stack gap={4}>{children}</Stack>
      </div>
    </AppShell>
  );
}
